// CopPage — NBS AI Community of Practice: Overview, Sessions, Meet the members.
//
// Overview is the home: a short numbers strip, orientation copy, and the photo.
// Sessions are a quiet log; a session with a recording shows an inline player.
import type { FormEvent, ReactNode } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { apiClient, ApiError } from "@/lib/api-client";
import { isSafeHttpUrl } from "@/lib/url";
import { useProfile } from "@/lib/useProfile";
import "./cop-page.css";

export interface CopRosterPerson {
  id: string;
  first_name: string;
  last_name: string;
  name: string;
  title: string;
  department: string;
  is_leadership: boolean;
  sort_order: number;
  initials: string;
  photo_url: string | null;
}

export type CopSessionStatus = "next" | "upcoming" | "waiting" | "ready";

export interface CopSessionItem {
  id: string;
  title: string;
  session_date: string;
  agenda: string;
  recap: string;
  status: CopSessionStatus;
  recording_url: string | null;
  notified_at: string | null;
}

export interface CopEmptyState {
  headline: string;
  body: string;
}

export interface CopPageResponse {
  headline: string;
  body: string;
  image_url?: string | null;
  years_in_tech?: number;
  years_at_nelnet?: number;
  years_in_ai?: number;
  years_as_of?: string;
  people_count?: number;
  members: CopRosterPerson[];
  sessions: CopSessionItem[];
  empty_state: CopEmptyState | null;
}

type Area = "about" | "sessions";
type SessionFilter = "all" | "upcoming" | "past";

const PHOTO_ACCEPT = "image/jpeg,image/png,image/webp";
const VIDEO_ACCEPT = "video/mp4,video/webm";

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

function formatYears(value: number | undefined): string {
  if (typeof value !== "number" || !Number.isFinite(value) || value < 0) return "0";
  const rounded = Math.round(value * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
}

function formatAsOf(yearMonth?: string): string {
  const match = yearMonth?.match(/^(\d{4})-(\d{2})$/);
  if (!match) return "";
  const month = Number(match[2]);
  if (month < 1 || month > 12) return "";
  return `${MONTH_NAMES[month - 1]} ${match[1]}`;
}

function isImageLine(line: string): boolean {
  if (!isSafeHttpUrl(line)) return false;
  try {
    return /\.(jpe?g|png|webp|gif)$/i.test(new URL(line).pathname);
  } catch {
    return false;
  }
}

function AboutImage({ src, testId }: { src: string; testId?: string }) {
  const [failed, setFailed] = useState(false);
  if (failed) return null;
  return (
    <figure className="cop-about-copy__figure">
      <img src={src} alt="" loading="lazy" onError={() => setFailed(true)} data-testid={testId} />
    </figure>
  );
}

function AboutBody({ body }: { body: string }) {
  const lines = body
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
  const elements: ReactNode[] = [];
  let listItems: string[] = [];

  const flushList = (key: string) => {
    if (listItems.length === 0) return;
    elements.push(
      <div key={`list-${key}`} className="cop-about-copy__list">
        {listItems.map((item, i) => (
          <div className="cop-about-copy__li" key={i}>
            {item}
          </div>
        ))}
      </div>,
    );
    listItems = [];
  };

  lines.forEach((line, i) => {
    if (line.startsWith("- ")) {
      listItems.push(line.slice(2));
      return;
    }
    flushList(String(i));
    if (line.startsWith("# ")) {
      elements.push(
        <h3 key={i} className="cop-about-copy__heading">
          {line.slice(2)}
        </h3>,
      );
      return;
    }
    if (isImageLine(line)) {
      elements.push(<AboutImage key={i} src={line} />);
      return;
    }
    elements.push(<p key={i}>{line}</p>);
  });
  flushList("end");

  return (
    <div className="cop-about-copy__body" data-testid="cop-about-body">
      {elements}
    </div>
  );
}

function fetchCop(): Promise<CopPageResponse> {
  return apiClient<CopPageResponse>("/api/v1/cop");
}

function isUpcomingStatus(status: CopSessionStatus): boolean {
  return status === "next" || status === "upcoming";
}

function statusLabel(status: CopSessionStatus): string | null {
  if (status === "next") return "Next session";
  if (status === "waiting") return "Recap coming";
  if (status === "ready") return "Recap";
  return null;
}

function sessionBody(session: CopSessionItem): string | null {
  if (session.status === "waiting") return "Recap will be posted shortly.";
  if (session.status === "ready") return session.recap.trim() ? session.recap : null;
  if (session.agenda.trim()) return session.agenda;
  return null;
}

function sessionKind(session: CopSessionItem): "Agenda" | "Recap" | null {
  if (session.status === "ready" && session.recap.trim()) return "Recap";
  if (isUpcomingStatus(session.status) && session.agenda.trim()) return "Agenda";
  return null;
}

function compareSessionDate(a: CopSessionItem, b: CopSessionItem): number {
  return a.session_date.localeCompare(b.session_date);
}

function formatSessionDate(isoDate: string): string {
  const [year, month, day] = isoDate.split("-").map(Number);
  if (!year || !month || !day) return isoDate;
  return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
}

function formatNotifiedDate(isoDateTime: string): string {
  const when = new Date(isoDateTime);
  if (Number.isNaN(when.getTime())) return isoDateTime;
  return when.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function MemberRow({
  person,
  isAdmin,
  onEdit,
  onRemove,
}: {
  person: CopRosterPerson;
  isAdmin: boolean;
  onEdit: (person: CopRosterPerson) => void;
  onRemove: (person: CopRosterPerson) => void;
}) {
  const [photoReady, setPhotoReady] = useState(false);
  return (
    <article className="cop-member" data-testid="cop-card" data-person-id={person.id}>
      <div className="cop-member__portrait">
        {person.photo_url ? (
          <>
            {!photoReady ? (
              <div className="cop-member__placeholder" aria-hidden="true">
                {person.initials}
              </div>
            ) : null}
            <img
              className={
                "cop-member__photo" +
                (photoReady ? " cop-member__photo--ready" : " cop-member__photo--pending")
              }
              src={person.photo_url}
              alt=""
              width={96}
              height={96}
              data-testid="cop-photo"
              onLoad={() => setPhotoReady(true)}
            />
          </>
        ) : (
          <div className="cop-member__placeholder" aria-hidden="true" data-testid="cop-placeholder">
            {person.initials}
          </div>
        )}
      </div>
      <div className="cop-member__meta">
        <div className="cop-member__name-row">
          <h3 className="cop-member__name">{person.name}</h3>
          {person.is_leadership ? <span className="cop-member__lead">Leadership</span> : null}
        </div>
        <p className="cop-member__title">{person.title}</p>
        {person.department.trim() ? <p className="cop-member__dept">{person.department}</p> : null}
      </div>
      {isAdmin && (
        <div className="cop-member__admin">
          <button type="button" onClick={() => onEdit(person)}>
            Edit
          </button>
          <button type="button" onClick={() => onRemove(person)}>
            Remove
          </button>
        </div>
      )}
    </article>
  );
}

function SessionCard({
  session,
  isAdmin,
  onEdit,
  onRemove,
  onNotify,
  notifying,
}: {
  session: CopSessionItem;
  isAdmin: boolean;
  onEdit: (session: CopSessionItem) => void;
  onRemove: (session: CopSessionItem) => void;
  onNotify: (session: CopSessionItem) => void;
  notifying: boolean;
}) {
  const label = statusLabel(session.status);
  const kind = sessionKind(session);
  const body = sessionBody(session);
  const quiet = session.status === "upcoming" || session.status === "waiting";
  const [showRecording, setShowRecording] = useState(false);
  return (
    <article className="cop-session" data-testid="cop-session" data-status={session.status}>
      <div className="cop-session__head">
        <h3 className="cop-session__title">{session.title}</h3>
        <div className="cop-session__flags">
          {label ? (
            <span
              className={
                "cop-session__pill" + (session.status === "next" ? " cop-session__pill--next" : "")
              }
            >
              {label}
            </span>
          ) : null}
          <span className="cop-session__date">{formatSessionDate(session.session_date)}</span>
        </div>
      </div>
      {kind ? <p className="cop-session__kind">{kind}</p> : null}
      {body ? (
        <p className={"cop-session__body" + (quiet ? " cop-session__body--quiet" : "")}>{body}</p>
      ) : null}
      {session.recording_url ? (
        <div className="cop-session__recording">
          <button
            type="button"
            className="cop-session__recording-toggle"
            aria-expanded={showRecording}
            data-testid="cop-recording-toggle"
            onClick={() => setShowRecording((open) => !open)}
          >
            {showRecording ? "Hide recording" : "Watch recording"}
          </button>
          {showRecording ? (
            <video
              className="cop-session__video"
              controls
              preload="metadata"
              playsInline
              src={session.recording_url}
            />
          ) : null}
        </div>
      ) : null}
      {isAdmin && (
        <div className="cop-session__admin">
          <button type="button" onClick={() => onEdit(session)}>
            Edit
          </button>
          <button type="button" onClick={() => onRemove(session)}>
            Remove
          </button>
          {session.notified_at ? (
            <span className="cop-session__notified" data-testid="cop-session-notified">
              Posted to Teams on {formatNotifiedDate(session.notified_at)}
            </span>
          ) : (
            <button
              type="button"
              data-testid="cop-session-notify"
              disabled={notifying}
              onClick={() => onNotify(session)}
            >
              {notifying ? "Posting…" : "Post to Teams"}
            </button>
          )}
        </div>
      )}
    </article>
  );
}

function SessionGroup({
  title,
  testId,
  sessions,
  showTitle,
  isAdmin,
  onEdit,
  onRemove,
  onNotify,
  notifyingIds,
}: {
  title: string;
  testId: string;
  sessions: CopSessionItem[];
  showTitle: boolean;
  isAdmin: boolean;
  onEdit: (session: CopSessionItem) => void;
  onRemove: (session: CopSessionItem) => void;
  onNotify: (session: CopSessionItem) => void;
  notifyingIds: ReadonlySet<string>;
}) {
  if (sessions.length === 0) return null;
  return (
    <div className="cop-session-group">
      {showTitle ? (
        <h3 className="cop-session-group__title" data-testid={testId}>
          {title}
        </h3>
      ) : null}
      <div className="cop-session-list">
        {sessions.map((session) => (
          <SessionCard
            key={session.id}
            session={session}
            isAdmin={isAdmin}
            onEdit={onEdit}
            onRemove={onRemove}
            onNotify={onNotify}
            notifying={notifyingIds.has(session.id)}
          />
        ))}
      </div>
    </div>
  );
}

export function CopPage() {
  const { isAdmin } = useProfile();
  const [data, setData] = useState<CopPageResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  // Kept apart from `error`: a failed action must not blank the page.
  const [actionError, setActionError] = useState<string | null>(null);
  const [area, setArea] = useState<Area>("about");
  const [filter, setFilter] = useState<SessionFilter>("all");
  const [aboutOpen, setAboutOpen] = useState(false);
  const [editingPerson, setEditingPerson] = useState<CopRosterPerson | "new" | null>(null);
  const [editingSession, setEditingSession] = useState<CopSessionItem | "new" | null>(null);
  const [notifyingIds, setNotifyingIds] = useState<ReadonlySet<string>>(new Set());

  const load = useCallback((showSpinner = false) => {
    if (showSpinner) setLoading(true);
    fetchCop()
      .then((page) => {
        setData(page);
        setError(null);
      })
      .catch((err: unknown) => setError(err instanceof Error ? err : new Error(String(err))))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load(true);
  }, [load]);

  const handleRemovePerson = useCallback(
    async (person: CopRosterPerson) => {
      if (!window.confirm(`Remove ${person.name}?`)) return;
      try {
        await apiClient(`/api/v1/admin/cop/roster/${encodeURIComponent(person.id)}`, {
          method: "DELETE",
        });
        load();
      } catch (err) {
        setActionError(err instanceof Error ? err.message : String(err));
      }
    },
    [load],
  );

  const handleRemoveSession = useCallback(
    async (session: CopSessionItem) => {
      if (!window.confirm(`Remove ${session.title}?`)) return;
      try {
        await apiClient(`/api/v1/admin/cop/sessions/${encodeURIComponent(session.id)}`, {
          method: "DELETE",
        });
        load();
      } catch (err) {
        setActionError(err instanceof Error ? err.message : String(err));
      }
    },
    [load],
  );

  const handleNotifySession = useCallback(
    async (session: CopSessionItem) => {
      if (!window.confirm(`Post "${session.title}" to the Community of Practice channel?`)) {
        return;
      }
      setActionError(null);
      // Block a second click while this session's post is still in flight.
      setNotifyingIds((ids) => new Set(ids).add(session.id));
      try {
        await apiClient(`/api/v1/admin/cop/sessions/${encodeURIComponent(session.id)}/notify`, {
          method: "POST",
        });
        load();
      } catch (err) {
        setActionError(err instanceof Error ? err.message : String(err));
      } finally {
        setNotifyingIds((ids) => {
          const next = new Set(ids);
          next.delete(session.id);
          return next;
        });
      }
    },
    [load],
  );

  const upcomingSessions = useMemo(() => {
    return (data?.sessions ?? [])
      .filter((s) => isUpcomingStatus(s.status))
      .slice()
      .sort(compareSessionDate);
  }, [data]);
  const pastSessions = useMemo(() => {
    return (data?.sessions ?? [])
      .filter((s) => !isUpcomingStatus(s.status))
      .slice()
      .sort((a, b) => compareSessionDate(b, a));
  }, [data]);
  const upcomingVisible = filter === "past" ? [] : upcomingSessions;
  const pastVisible = filter === "upcoming" ? [] : pastSessions;
  const hasSessions = upcomingVisible.length + pastVisible.length > 0;

  return (
    <div className="cop-page" data-testid="cop-page">
      <div className="cop-page__intro">
        <div>
          <div className="cop-page__eyebrow">NBS AI</div>
          <h1 className="cop-page__title">Community of Practice</h1>
        </div>
        <div className="cop-page__areas" role="tablist" aria-label="CoP areas">
          <button
            type="button"
            className={"cop-pill" + (area === "about" ? " cop-pill--active" : "")}
            data-testid="cop-area-about"
            aria-selected={area === "about"}
            onClick={() => setArea("about")}
          >
            Overview
          </button>
          <button
            type="button"
            className={"cop-pill" + (area === "sessions" ? " cop-pill--active" : "")}
            data-testid="cop-area-sessions"
            aria-selected={area === "sessions"}
            onClick={() => setArea("sessions")}
          >
            Sessions
          </button>
        </div>
      </div>

      {loading && (
        <div className="cop-wait" role="status" aria-live="polite" data-testid="cop-loading">
          <div className="cop-wait__pulse" aria-hidden="true" />
          <p className="cop-wait__headline">Settling the room.</p>
          <p className="cop-wait__body">Portraits and sessions are on the way.</p>
          <div className="cop-wait__skeleton" aria-hidden="true">
            <div className="cop-wait__skel-main" />
            <div className="cop-wait__skel-card" />
            <div className="cop-wait__skel-card" />
          </div>
        </div>
      )}

      {error && !loading && (
        <div className="cop-error" role="alert">
          <strong>Could not load the CoP page.</strong>
          <p>{error.message}</p>
        </div>
      )}

      {actionError && (
        <div className="cop-action-error" role="status" data-testid="cop-action-error">
          <span>{actionError}</span>
          <button type="button" onClick={() => setActionError(null)} aria-label="Dismiss">
            Dismiss
          </button>
        </div>
      )}

      {!loading && !error && data && (
        <div className="cop-page__layout">
          <div className="cop-page__main">
            {area === "about" ? (
              <section data-testid="cop-about">
                {isAdmin && (
                  <div className="cop-about-actions">
                    <button
                      type="button"
                      className="hub-newpost"
                      data-testid="cop-edit-about"
                      onClick={() => setAboutOpen(true)}
                    >
                      Edit
                    </button>
                  </div>
                )}
                <div className="cop-numbers" data-testid="cop-stats">
                  <div className="cop-numbers__head">
                    <p className="cop-numbers__title">By the numbers</p>
                    {formatAsOf(data.years_as_of) ? (
                      <p className="cop-numbers__as-of">As of {formatAsOf(data.years_as_of)}</p>
                    ) : null}
                  </div>
                  <div className="cop-numbers__grid">
                    <span className="cop-numbers__fact" data-testid="cop-stat-people">
                      <span className="cop-numbers__value cop-numbers__value--lead">
                        {data.people_count ?? 0}
                      </span>
                      <span className="cop-numbers__label">people</span>
                    </span>
                    <span className="cop-numbers__fact" data-testid="cop-stat-tech">
                      <span className="cop-numbers__value">{formatYears(data.years_in_tech)}</span>
                      <span className="cop-numbers__label">years in tech</span>
                    </span>
                    <span className="cop-numbers__fact" data-testid="cop-stat-nelnet">
                      <span className="cop-numbers__value">
                        {formatYears(data.years_at_nelnet)}
                      </span>
                      <span className="cop-numbers__label">years at Nelnet</span>
                    </span>
                    <span className="cop-numbers__fact" data-testid="cop-stat-ai">
                      <span className="cop-numbers__value">{formatYears(data.years_in_ai)}</span>
                      <span className="cop-numbers__label">years in AI</span>
                    </span>
                  </div>
                </div>
                <h2 className="cop-section__title cop-section__title--about">
                  {data.headline.trim() ? data.headline : "Who this room is"}
                </h2>
                {data.body.trim() || data.image_url ? (
                  <div className="cop-about-copy" data-testid="cop-about-copy">
                    {data.body.trim() ? <AboutBody body={data.body} /> : null}
                    {data.image_url ? (
                      <AboutImage src={data.image_url} testId="cop-about-photo" />
                    ) : null}
                  </div>
                ) : data.headline.trim() ? null : (
                  <p className="cop-quiet">Who this group is will appear here.</p>
                )}
              </section>
            ) : (
              <section data-testid="cop-sessions">
                <div className="cop-section__head">
                  <h2 className="cop-section__title">Sessions</h2>
                  <div className="cop-page__filters" role="group" aria-label="Session filter">
                    {(["all", "upcoming", "past"] as const).map((id) => (
                      <button
                        key={id}
                        type="button"
                        className={
                          "cop-pill cop-pill--sm" + (filter === id ? " cop-pill--active" : "")
                        }
                        data-testid={`cop-filter-${id}`}
                        onClick={() => setFilter(id)}
                      >
                        {id === "all" ? "All" : id === "upcoming" ? "Upcoming" : "Past"}
                      </button>
                    ))}
                  </div>
                  {isAdmin && (
                    <button
                      type="button"
                      className="hub-newpost"
                      data-testid="cop-add-session"
                      onClick={() => setEditingSession("new")}
                    >
                      Add session
                    </button>
                  )}
                </div>
                {hasSessions ? (
                  <div className="cop-session-groups">
                    <SessionGroup
                      title="Upcoming"
                      testId="cop-session-group-upcoming"
                      sessions={upcomingVisible}
                      showTitle={filter === "all"}
                      isAdmin={isAdmin}
                      onEdit={setEditingSession}
                      onRemove={handleRemoveSession}
                      onNotify={handleNotifySession}
                      notifyingIds={notifyingIds}
                    />
                    <SessionGroup
                      title="Past"
                      testId="cop-session-group-past"
                      sessions={pastVisible}
                      showTitle={filter === "all"}
                      isAdmin={isAdmin}
                      onEdit={setEditingSession}
                      onRemove={handleRemoveSession}
                      onNotify={handleNotifySession}
                      notifyingIds={notifyingIds}
                    />
                  </div>
                ) : (
                  <p className="cop-quiet" data-testid="cop-sessions-empty">
                    Sessions will appear here.
                  </p>
                )}
              </section>
            )}
          </div>

          <aside className="cop-page__members" data-testid="cop-members">
            <div className="cop-section__head">
              <h2 className="cop-section__title">Meet the members</h2>
              {isAdmin && (
                <button
                  type="button"
                  className="hub-newpost"
                  data-testid="cop-add-person"
                  onClick={() => setEditingPerson("new")}
                >
                  Add person
                </button>
              )}
            </div>
            {data.members.length > 0 ? (
              <div className="cop-member-list">
                {data.members.map((p) => (
                  <MemberRow
                    key={p.id}
                    person={p}
                    isAdmin={isAdmin}
                    onEdit={setEditingPerson}
                    onRemove={handleRemovePerson}
                  />
                ))}
              </div>
            ) : data.empty_state ? (
              <div className="cop-empty" data-testid="cop-empty-state" role="status">
                <h3>{data.empty_state.headline}</h3>
                <p>{data.empty_state.body}</p>
              </div>
            ) : null}
          </aside>
        </div>
      )}

      {aboutOpen && data && (
        <AboutModal
          headline={data.headline}
          body={data.body}
          hasPhoto={Boolean(data.image_url)}
          onClose={() => setAboutOpen(false)}
          onSaved={() => {
            setAboutOpen(false);
            load();
          }}
        />
      )}

      {editingPerson !== null && (
        <PersonModal
          person={editingPerson === "new" ? null : editingPerson}
          onClose={() => setEditingPerson(null)}
          onSaved={() => {
            setEditingPerson(null);
            load();
          }}
        />
      )}

      {editingSession !== null && (
        <SessionModal
          session={editingSession === "new" ? null : editingSession}
          onClose={() => setEditingSession(null)}
          onSaved={() => {
            setEditingSession(null);
            load();
          }}
        />
      )}
    </div>
  );
}

function AboutModal({
  headline,
  body,
  hasPhoto,
  onClose,
  onSaved,
}: {
  headline: string;
  body: string;
  hasPhoto: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [draftHeadline, setDraftHeadline] = useState(headline);
  const [draftBody, setDraftBody] = useState(body);
  const [file, setFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setFormError(null);
    try {
      await apiClient("/api/v1/admin/cop", {
        method: "PUT",
        body: JSON.stringify({
          headline: draftHeadline,
          body: draftBody,
        }),
      });
      if (file) {
        const form = new FormData();
        form.append("file", file);
        await apiClient("/api/v1/admin/cop/image", {
          method: "POST",
          body: form,
        });
      }
      onSaved();
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : "Could not save Overview.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className="hub-modal-overlay"
      onClick={saving ? undefined : onClose}
      data-testid="cop-about-modal"
    >
      <div
        className="hub-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Edit Overview"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="hub-modal__head">
          <h2 className="hub-modal__title">Edit Overview</h2>
          <button
            type="button"
            className="hub-modal__close"
            aria-label="Close"
            onClick={onClose}
            disabled={saving}
          >
            ×
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <label className="cop-compose-label" htmlFor="cop-headline">
            Heading
          </label>
          <input
            id="cop-headline"
            className="cop-compose-input"
            value={draftHeadline}
            onChange={(e) => setDraftHeadline(e.target.value)}
          />
          <label className="cop-compose-label" htmlFor="cop-body">
            Overview
          </label>
          <textarea
            id="cop-body"
            className="cop-compose-textarea"
            value={draftBody}
            onChange={(e) => setDraftBody(e.target.value)}
            placeholder="One line per paragraph. # Heading. - bullet."
          />
          <label className="cop-compose-label" htmlFor="cop-about-photo">
            Photo (jpeg, png, or webp)
          </label>
          <input
            id="cop-about-photo"
            type="file"
            accept={PHOTO_ACCEPT}
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
          {hasPhoto && !file ? (
            <p className="cop-quiet">
              A photo is already on the page. Choose a file to replace it.
            </p>
          ) : null}
          {formError && <p className="cop-compose-error">{formError}</p>}
          {saving ? (
            <p className="cop-wait cop-wait--inline" role="status">
              {file ? "A photo can take a minute to land. Keep this window open." : "Saving."}
            </p>
          ) : null}
          <div className="cop-compose-actions">
            <button type="button" className="hub-newpost" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="hub-newpost" disabled={saving}>
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function PersonModal({
  person,
  onClose,
  onSaved,
}: {
  person: CopRosterPerson | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isNew = person === null;
  const [firstName, setFirstName] = useState(person?.first_name ?? "");
  const [lastName, setLastName] = useState(person?.last_name ?? "");
  const [title, setTitle] = useState(person?.title ?? "");
  const [department, setDepartment] = useState(person?.department ?? "");
  const [isLeadership, setIsLeadership] = useState(person?.is_leadership ?? false);
  const [file, setFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setFormError(null);
    try {
      let personId = person?.id;
      if (isNew) {
        const created = await apiClient<CopRosterPerson>("/api/v1/admin/cop/roster", {
          method: "POST",
          body: JSON.stringify({
            first_name: firstName.trim(),
            last_name: lastName.trim(),
            title: title.trim(),
            department: department.trim(),
            is_leadership: isLeadership,
          }),
        });
        personId = created.id;
      } else {
        await apiClient(`/api/v1/admin/cop/roster/${encodeURIComponent(person.id)}`, {
          method: "PATCH",
          body: JSON.stringify({
            first_name: firstName.trim(),
            last_name: lastName.trim(),
            title: title.trim(),
            department: department.trim(),
            is_leadership: isLeadership,
          }),
        });
      }
      if (file && personId) {
        const form = new FormData();
        form.append("file", file);
        await apiClient(`/api/v1/admin/cop/roster/${encodeURIComponent(personId)}/photo`, {
          method: "POST",
          body: form,
        });
      }
      onSaved();
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : "Could not save this person.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className="hub-modal-overlay"
      onClick={saving ? undefined : onClose}
      data-testid="cop-person-modal"
    >
      <div
        className="hub-modal"
        role="dialog"
        aria-modal="true"
        aria-label={isNew ? "Add person" : "Edit person"}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="hub-modal__head">
          <h2 className="hub-modal__title">{isNew ? "Add person" : "Edit person"}</h2>
          <button
            type="button"
            className="hub-modal__close"
            aria-label="Close"
            onClick={onClose}
            disabled={saving}
          >
            ×
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <label className="cop-compose-label" htmlFor="cop-first-name">
            First name
          </label>
          <input
            id="cop-first-name"
            className="cop-compose-input"
            required
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
          />
          <label className="cop-compose-label" htmlFor="cop-last-name">
            Last name
          </label>
          <input
            id="cop-last-name"
            className="cop-compose-input"
            required
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
          />
          <label className="cop-compose-label" htmlFor="cop-job-title">
            Title
          </label>
          <input
            id="cop-job-title"
            className="cop-compose-input"
            required
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
          <label className="cop-compose-label" htmlFor="cop-department">
            Department
          </label>
          <input
            id="cop-department"
            className="cop-compose-input"
            value={department}
            onChange={(e) => setDepartment(e.target.value)}
          />
          <label className="cop-compose-check">
            <input
              type="checkbox"
              checked={isLeadership}
              onChange={(e) => setIsLeadership(e.target.checked)}
            />
            Leadership
          </label>
          <label className="cop-compose-label" htmlFor="cop-photo">
            Headshot (jpeg, png, or webp)
          </label>
          <input
            id="cop-photo"
            type="file"
            accept={PHOTO_ACCEPT}
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
          {formError && <p className="cop-compose-error">{formError}</p>}
          {saving ? (
            <p className="cop-wait cop-wait--inline" role="status">
              {file ? "A photo can take a minute to land. Keep this window open." : "Saving."}
            </p>
          ) : null}
          <div className="cop-compose-actions">
            <button type="button" className="hub-newpost" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="hub-newpost" disabled={saving}>
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function SessionModal({
  session,
  onClose,
  onSaved,
}: {
  session: CopSessionItem | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isNew = session === null;
  const [title, setTitle] = useState(session?.title ?? "");
  const [sessionDate, setSessionDate] = useState(session?.session_date ?? "");
  const [agenda, setAgenda] = useState(session?.agenda ?? "");
  const [recap, setRecap] = useState(session?.recap ?? "");
  const [file, setFile] = useState<File | null>(null);
  const hasRecording = Boolean(session?.recording_url);
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const busy = saving || removing;

  async function handleRemoveRecording() {
    if (isNew || session === null) return;
    setRemoving(true);
    setFormError(null);
    try {
      await apiClient(`/api/v1/admin/cop/sessions/${encodeURIComponent(session.id)}/recording`, {
        method: "DELETE",
      });
      onSaved();
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : "Could not remove the recording.");
    } finally {
      setRemoving(false);
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setFormError(null);
    try {
      let sessionId = isNew ? null : session.id;
      if (isNew) {
        const created = await apiClient<CopSessionItem>("/api/v1/admin/cop/sessions", {
          method: "POST",
          body: JSON.stringify({
            title: title.trim(),
            session_date: sessionDate,
            agenda,
            recap,
          }),
        });
        sessionId = created.id;
      } else {
        await apiClient(`/api/v1/admin/cop/sessions/${encodeURIComponent(session.id)}`, {
          method: "PATCH",
          body: JSON.stringify({
            title: title.trim(),
            session_date: sessionDate,
            agenda,
            recap,
          }),
        });
      }
      if (file && sessionId) {
        const form = new FormData();
        form.append("file", file);
        await apiClient(`/api/v1/admin/cop/sessions/${encodeURIComponent(sessionId)}/recording`, {
          method: "POST",
          body: form,
        });
      }
      onSaved();
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : "Could not save this session.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className="hub-modal-overlay"
      onClick={busy ? undefined : onClose}
      data-testid="cop-session-modal"
    >
      <div
        className="hub-modal"
        role="dialog"
        aria-modal="true"
        aria-label={isNew ? "Add session" : "Edit session"}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="hub-modal__head">
          <h2 className="hub-modal__title">{isNew ? "Add session" : "Edit session"}</h2>
          <button
            type="button"
            className="hub-modal__close"
            aria-label="Close"
            onClick={onClose}
            disabled={busy}
          >
            ×
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <label className="cop-compose-label" htmlFor="cop-session-title">
            Title
          </label>
          <input
            id="cop-session-title"
            className="cop-compose-input"
            required
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
          <label className="cop-compose-label" htmlFor="cop-session-date">
            Date
          </label>
          <input
            id="cop-session-date"
            className="cop-compose-input"
            type="date"
            required
            value={sessionDate}
            onChange={(e) => setSessionDate(e.target.value)}
          />
          <label className="cop-compose-label" htmlFor="cop-session-agenda">
            Agenda
          </label>
          <textarea
            id="cop-session-agenda"
            className="cop-compose-textarea cop-compose-textarea--short"
            value={agenda}
            onChange={(e) => setAgenda(e.target.value)}
            placeholder="Leave blank to omit the agenda line."
          />
          <label className="cop-compose-label" htmlFor="cop-session-recap">
            Recap
          </label>
          <textarea
            id="cop-session-recap"
            className="cop-compose-textarea"
            value={recap}
            onChange={(e) => setRecap(e.target.value)}
            placeholder="Leave blank after the date to show Recap will be posted shortly."
          />
          <label className="cop-compose-label" htmlFor="cop-session-recording">
            Recording (mp4 or webm)
          </label>
          {hasRecording ? (
            <div className="cop-session-recording-current">
              <span className="cop-quiet">
                Recording attached. Uploading a new file replaces it.
              </span>
              <button
                type="button"
                className="cop-session-recording-remove"
                data-testid="cop-remove-recording"
                onClick={handleRemoveRecording}
                disabled={busy}
              >
                {removing ? "Removing…" : "Remove recording"}
              </button>
            </div>
          ) : null}
          <input
            id="cop-session-recording"
            className="cop-compose-input"
            type="file"
            accept={VIDEO_ACCEPT}
            data-testid="cop-session-recording"
            disabled={busy}
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
          {formError && <p className="cop-compose-error">{formError}</p>}
          {saving ? (
            <p className="cop-wait cop-wait--inline" role="status">
              Saving.
            </p>
          ) : null}
          <div className="cop-compose-actions">
            <button type="button" className="hub-newpost" onClick={onClose} disabled={busy}>
              Cancel
            </button>
            <button type="submit" className="hub-newpost" disabled={busy}>
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
